import React, { useEffect } from 'react';
import { Form, Input, Modal, Select } from 'antd';

import type {
	ProductionQuestionResolutionCategory,
	ProductionQuestionResolutionSource,
} from '../../../interfaces/ProductionQuestion.interface';
import {
	RESOLUTION_CATEGORY_OPTIONS,
	RESOLUTION_SOURCE_OPTIONS,
} from './constants';

export interface ResolutionFormValues {
	resolution_category: ProductionQuestionResolutionCategory;
	resolution_source: ProductionQuestionResolutionSource;
	resolution_description: string;
}

interface ResolutionModalProps {
	open: boolean;
	initialCategory?: ProductionQuestionResolutionCategory | '';
	initialSource?: ProductionQuestionResolutionSource | '';
	initialDescription?: string;
	confirmLoading?: boolean;
	onCancel: () => void;
	onSubmit: (values: ResolutionFormValues) => void | Promise<void>;
}

const ResolutionModal: React.FC<ResolutionModalProps> = ({
	open,
	initialCategory = '',
	initialSource = '',
	initialDescription = '',
	confirmLoading = false,
	onCancel,
	onSubmit,
}) => {
	const [form] = Form.useForm<ResolutionFormValues>();

	useEffect(() => {
		if (!open) return;
		form.setFieldsValue({
			resolution_category: initialCategory || undefined,
			resolution_source: initialSource || undefined,
			resolution_description: initialDescription,
		});
	}, [open, initialCategory, initialSource, initialDescription, form]);

	return (
		<Modal
			title="Resolve question"
			open={open}
			onCancel={onCancel}
			onOk={() => form.submit()}
			okText="Mark resolved"
			confirmLoading={confirmLoading}
			destroyOnClose
		>
			<p className="mb-4 text-sm text-neutral-600">
				Select how this question was resolved and describe the outcome.
			</p>
			<Form form={form} layout="vertical" onFinish={onSubmit}>
				<Form.Item
					name="resolution_source"
					label="Resolution source"
					rules={[{ required: true, message: 'Select who resolved this question' }]}
				>
					<Select
						placeholder="Select resolution source"
						options={RESOLUTION_SOURCE_OPTIONS}
					/>
				</Form.Item>
				<Form.Item
					name="resolution_category"
					label="Resolution category"
					rules={[{ required: true, message: 'Select a resolution category' }]}
				>
					<Select
						placeholder="Select resolution category"
						options={RESOLUTION_CATEGORY_OPTIONS}
					/>
				</Form.Item>
				<Form.Item
					name="resolution_description"
					label="Resolution"
					rules={[
						{
							required: true,
							message: 'Explain how this question was resolved',
						},
					]}
				>
					<Input.TextArea
						rows={4}
						placeholder="For example: Customer verbally approved the 3D dimensions."
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default ResolutionModal;
